import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_KEYS } from "@/lib/categories";
import { Bell, Flame, Check, Clock, Music, Moon } from "lucide-react";

export default function Alerts() {
  const { t, tCategory } = useLang();
  const { user, loading } = useAuth();
  const [categories, setCategories] = useState([]);
  const [cantons, setCantons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [leadHours, setLeadHours] = useState(24);
  const [nordic, setNordic] = useState(false);
  const [newMoon, setNewMoon] = useState(false);

  const { data: prefs } = useQuery({ queryKey: ["alertPrefs"], queryFn: async () => (await api.get("/alerts")).data, enabled: !!user });
  const { data: cantonList = [] } = useQuery({ queryKey: ["cantons"], queryFn: async () => (await api.get("/cantons")).data });
  const { data: matches = [], refetch } = useQuery({ queryKey: ["alertMatches"], queryFn: async () => (await api.get("/alerts/matches")).data, enabled: !!user });

  useEffect(() => { if (prefs) { setCategories(prefs.categories || []); setCantons(prefs.cantons || []); setNordic(prefs.nordic_concerts || false); } }, [prefs]);
  useEffect(() => { if (user) { setLeadHours(user.reminder_lead_hours || 24); setNewMoon(user.new_moon_reminder || false); } }, [user]);

  const saveLead = async (h) => {
    setLeadHours(h);
    try { await api.put("/me/settings", { reminder_lead_hours: h }); toast.success(t("reminder_saved")); } catch { toast.error("Error"); }
  };
  const toggleNewMoon = async () => {
    const v = !newMoon; setNewMoon(v);
    try { await api.put("/me/settings", { new_moon_reminder: v, reminder_lead_hours: leadHours }); toast.success(t("reminder_saved")); }
    catch { setNewMoon(!v); toast.error(t("aw_error")); }
  };

  const toggle = (arr, setArr, val) => setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/alerts", { categories, cantons, nordic_concerts: nordic });
      toast.success(t("alerts_saved"));
      refetch();
    } catch { toast.error(t("aw_error")); } finally { setSaving(false); }
  };

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/alerts")}`;
  };

  if (loading) return <div className="py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!user) return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <Flame className="w-12 h-12 text-amber-500 mx-auto mb-6" />
      <h1 className="font-serif text-2xl text-amber-100 mb-4">{t("alerts_title")}</h1>
      <button data-testid="alerts-login-btn" onClick={handleLogin} className="bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-3 rounded-md">{t("login_google")}</button>
    </div>
  );

  const chip = (active) => `px-4 py-2 rounded-full text-sm border transition-all ${active ? "bg-amber-600 text-stone-950 font-semibold border-amber-400" : "bg-[#121418] text-stone-300 border-[#272A34] hover:border-amber-500/40"}`;

  return (
    <>
      <Seo title={t("alerts_title")} canonicalPath="/alerts" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex items-center gap-3 mb-3"><Bell className="w-7 h-7 text-amber-500" /><h1 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight text-amber-100">{t("alerts_title")}</h1></div>
        <p className="text-stone-400 mb-10 max-w-2xl">{t("alerts_desc")}</p>

        <div className="bg-[#121418] border border-[#272A34] rounded-xl p-6 sm:p-8 space-y-8">
          <div>
            <h3 className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-4">{t("alerts_pick_cat")}</h3>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_KEYS.map((c) => <button key={c} data-testid={`alert-cat-${c}`} onClick={() => toggle(categories, setCategories, c)} className={chip(categories.includes(c))}>{tCategory(c)}</button>)}
            </div>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-4">{t("alerts_pick_canton")}</h3>
            <div className="flex flex-wrap gap-2">
              {cantonList.map((c) => <button key={c.key} data-testid={`alert-canton-${c.key}`} onClick={() => toggle(cantons, setCantons, c.key)} className={chip(cantons.includes(c.key))}>{c.key}</button>)}
            </div>
          </div>
          <div data-testid="alerts-nordic-block" className="pt-2 border-t border-[#272A34]">
            <h3 className="font-mono text-xs uppercase tracking-wider text-indigo-300/90 mb-3 flex items-center gap-2"><Music className="w-4 h-4" /> {t("alerts_nordic")}</h3>
            <button data-testid="alert-nordic-toggle" onClick={() => setNordic(!nordic)} className={chip(nordic)}>{nordic ? t("alerts_nordic_on") : t("alerts_nordic_off")}</button>
            <p className="text-xs text-stone-500 mt-2 max-w-xl">{t("alerts_nordic_desc")}</p>
          </div>
          <button data-testid="alerts-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 font-semibold px-7 py-3 rounded-md shadow-lg transition-all disabled:opacity-60 border border-amber-400/30">
            <Check className="w-4 h-4" /> {saving ? "…" : t("alerts_save")}
          </button>
        </div>

        <div className="bg-[#121418] border border-[#272A34] rounded-xl p-6 sm:p-8 mt-6">
          <h3 className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-4 flex items-center gap-2"><Clock className="w-4 h-4" /> {t("reminder_timing")}</h3>
          <div className="flex flex-wrap gap-2">
            {[12, 24, 48, 72].map((h) => (
              <button key={h} data-testid={`reminder-lead-${h}`} onClick={() => saveLead(h)} className={chip(leadHours === h)}>{h}h {t("hours_before")}</button>
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-[#272A34]" data-testid="alerts-newmoon-block">
            <h3 className="font-mono text-xs uppercase tracking-wider text-indigo-300/90 mb-3 flex items-center gap-2"><Moon className="w-4 h-4" /> {t("alerts_newmoon")}</h3>
            <button data-testid="alert-newmoon-toggle" onClick={toggleNewMoon} className={chip(newMoon)}>{newMoon ? t("alerts_newmoon_on") : t("alerts_newmoon_off")}</button>
            <p className="text-xs text-stone-500 mt-2 max-w-xl">{t("alerts_newmoon_desc")}</p>
          </div>
        </div>

        <section className="mt-14">
          <h2 className="font-serif text-2xl text-stone-100 mb-6 border-b border-amber-900/30 pb-4">{t("alerts_matches")} ({matches.length})</h2>
          {matches.length === 0 ? (
            <div className="text-center py-16 text-stone-400 border border-dashed border-[#272A34] rounded-lg" data-testid="alerts-no-matches">{t("alerts_none")}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{matches.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}</div>
          )}
        </section>
      </div>
    </>
  );
}
